import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useSnackbar } from 'notistack';
import moment from 'moment/moment';
import { styles } from './styles';
import DateCalendar from '../../components/common/DateCalendar';
import { useBulkUpdateChangeRequestStatusesMutation } from '../../api/changeRequests';

export const ChangeRequestBulkActionMenu = ({
    Component,
    tab,
    selectedCount,
    label,
    selectedCRs,
    selectedPosition,
    handleBulkAction,
}) => {
    const [actionType, setActionType] = useState();
    const [comment, setComment] = useState('');
    const [modalType, setModalType] = useState(1);
    const [startDate, setStartDate] = useState();
    const [selectedCRPosition, setSelectedCRPosition] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    const [bulkUpdateChangeRequestStatuses, { isLoading: isBulkUpdateLoading }] =
        useBulkUpdateChangeRequestStatusesMutation();

    useEffect(() => {
        setModalType(1);
        setStartDate();
        setComment('');
        setSelectedCRPosition(selectedPosition.map((item) => item));
    }, [actionType]);

    const handleConfirmStatus = async (status) => {
        setActionType();
        const body = new FormData();
        body.append('status', status);
        body.append('ids', selectedCRs.join(', '));

        if (comment?.length > 0) body.append('comment', comment);
        if (startDate) {
            body.append('startDate', startDate);
            body.append(
                'updateStartDateChangeRequestIds',
                selectedCRPosition?.map((item) => item.changeRequestId).join(', '),
            );
        }

        try {
            const response = await bulkUpdateChangeRequestStatuses(body).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message || 'Bulk update completed successfully!', { variant: 'success' });
            handleBulkAction();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Failed to update change requests.', { variant: 'error' });
        }
    };

    const handlePositionSelected = (event, data) => {
        const isChecked = event.target.checked;
        if (isChecked) setSelectedCRPosition((prev) => [...prev, data]);
        else setSelectedCRPosition((prev) => prev.filter((item) => item.id !== data?.id));
    };

    const dialogContent = useMemo(() => {
        if (actionType === 'approve') {
            switch (modalType) {
                case 1:
                    return {
                        title: 'Bulk approve change requests',
                        subTitle: `Are you sure you want to approve ${selectedCount} change requests?`,
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        return selectedPosition?.length > 0
                                            ? setModalType(2)
                                            : handleConfirmStatus('Approved');
                                    }}
                                >
                                    {selectedPosition?.length > 0 ? `Next` : `Approve`}
                                </Button>
                            </>
                        ),
                    };
                case 2:
                    return {
                        title: 'Reset Start Date',
                        subTitle: `Would you like to reset the start date for ${selectedPosition?.length} positions created as Major Change Requests?`,
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => handleConfirmStatus('Approved')}>
                                    Skip
                                </Button>
                                <Button variant="contained" color="primary" onClick={() => setModalType(3)}>
                                    Yes
                                </Button>
                            </>
                        ),
                    };
                case 3:
                    return {
                        title: 'Update Positions Start Date',
                        subTitle: '',
                        children: (
                            <Box>
                                <Box sx={styles.infoLine}></Box>
                                <Box sx={styles.infoLine}></Box>
                                <Typography variant="subtitle1">Select the updated date from calendar</Typography>
                                <DateCalendar
                                    value={startDate}
                                    onChange={(_key, value) => setStartDate(value)}
                                    disableFuture={true}
                                />
                            </Box>
                        ),
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={!startDate}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setModalType(4)}
                                >
                                    Next
                                </Button>
                            </>
                        ),
                    };
                case 4:
                    return {
                        title: 'Approve Change Request and Confirm Positions Start Date',
                        subTitle: `Are you sure you want to approve ${selectedCount} Change Request and update positions details of`,
                        children: (
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant={'body2'}
                                            mx="24px"
                                            color={'#3F455A'}
                                        >{`Updated Start Date: ${startDate ? moment(startDate).format('MMM DD yyyy') : '-'}`}</Typography>
                                        <Box sx={styles.infoLine}></Box>
                                    </Grid>
                                </Grid>
                                <Grid container sx={styles.checkboxWrapper}>
                                    {selectedPosition.map((item) => (
                                        <Grid key={item.id} item xs={4} sx={styles.checkbox} mt={1}>
                                            <Checkbox
                                                color="secondary"
                                                checked={selectedCRPosition.indexOf(item) >= 0}
                                                disabled={false}
                                                onChange={(e) => handlePositionSelected(e, item)}
                                            />
                                            <Typography color="textPrimary" sx={styles.typography}>
                                                {item.positionCode}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ),
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleConfirmStatus('Approved')}
                                >
                                    Confirm
                                </Button>
                            </>
                        ),
                    };
            }
        } else if (actionType === 'edit')
            return {
                title: 'Modify Change Requests',
                children: (
                    <React.Fragment>
                        <FormControl fullWidth>
                            <InputLabel id="hm">Hiring Manager</InputLabel>
                            <Select
                                labelId="hm"
                                id="demo-simple-select"
                                // value={}
                                label="Hiring Manager"
                                // onChange={handleChange}
                            >
                                <MenuItem value="reason">Reason</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl
                            fullWidth
                            sx={{
                                mt: '0px !important',
                            }}
                        >
                            <InputLabel id="rm">Recruitment manager</InputLabel>
                            <Select
                                labelId="rm"
                                id="demo-simple-select"
                                // value={}
                                label="Recruitment manager"
                                // onChange={handleChange}
                            >
                                <MenuItem value="reason">Reason</MenuItem>
                            </Select>
                        </FormControl>
                    </React.Fragment>
                ),
                actions: (
                    <React.Fragment>
                        <Button size="large" onClick={() => setActionType()}>
                            Cancel
                        </Button>
                        <Button size="large" variant="contained">
                            Confirm
                        </Button>
                    </React.Fragment>
                ),
            };
        else if (actionType === 'reject')
            return {
                title: 'Bulk Reject Change Requests',
                subTitle: `Are you sure you want to reject ${selectedCount} change requests?`,
                children: (
                    <TextField
                        fullWidth
                        sx={{ mt: '15px' }}
                        label="Comment"
                        placeholder="Type here"
                        size="small"
                        variant="outlined"
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        rows={5}
                    />
                ),
                actions: (
                    <React.Fragment>
                        <Button size="large" onClick={() => setActionType()}>
                            Cancel
                        </Button>
                        <Button size="large" variant="contained" onClick={() => handleConfirmStatus('Rejected')}>
                            Confirm
                        </Button>
                    </React.Fragment>
                ),
            };
    }, [actionType, selectedCount, comment, modalType, startDate, selectedCRPosition]);

    const actionItems =
        tab === 'approver'
            ? [
                  {
                      label: 'Approve',
                      type: 'approve',
                      icon: <CheckOutlinedIcon />,
                      onClick: (action) => setActionType(action.type),
                  },
                  {
                      label: 'Reject',
                      type: 'reject',
                      icon: <CloseIcon />,
                      onClick: (action) => setActionType(action.type),
                  },
                  // {
                  //   label: 'Edit',
                  //   type: 'edit',
                  //   icon: <EditIcon />,
                  //   onClick: (action) => setActionType(action.type),
                  // },
              ]
            : [];

    return (
        <Component label={label} actions={actionItems} dialog={dialogContent} onDialogClose={() => setActionType()} />
    );
};
